import { CompanyProfileType } from '@/api/backend/types';
import { Badge, ListTable, ListTableCell, ListTableHeader, ListTableRow } from '@opaz/ui-components';
import { createFileRoute } from '@tanstack/react-router';
import { useContext } from 'react';
import { ProfileContext } from '../route';
import { useTranslation } from 'react-i18next';
import { formatIsoDate } from '@/lib/utils';

export const Route = createFileRoute('/_auth/profile/company/_layout/cr-information')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t, i18n } = useTranslation();
  const { profile } = useContext(ProfileContext);
  const companyProfile = profile as CompanyProfileType;

  return (
    <ListTable>
      <ListTableRow>
        <ListTableHeader>{t('profile.company.tabs.crInformation.listTable.companyName')}</ListTableHeader>
        <ListTableCell>{companyProfile.companyDetails.companyName}</ListTableCell>
      </ListTableRow>

      <ListTableRow>
        <ListTableHeader>{t('profile.company.tabs.crInformation.listTable.companyNameAr')}</ListTableHeader>
        <ListTableCell>{companyProfile.companyDetails.companyNameAr}</ListTableCell>
      </ListTableRow>

      <ListTableRow>
        <ListTableHeader>{t('profile.company.tabs.crInformation.listTable.commericalRegistration')}</ListTableHeader>
        <ListTableCell>{companyProfile.companyDetails.crNumber}</ListTableCell>
      </ListTableRow>

      <ListTableRow>
        <ListTableHeader>{t('profile.company.tabs.crInformation.listTable.address')}</ListTableHeader>
        <ListTableCell>
          {companyProfile.companyDetails.address && (
            <>
              <div className="flex flex-col">
                <span>
                  {i18n.language === 'ar'
                    ? [
                        companyProfile.companyDetails.addressAr.state,
                        companyProfile.companyDetails.addressAr.townName,
                        companyProfile.companyDetails.addressAr.street,
                        companyProfile.companyDetails.addressAr.buildingNumber,
                      ]
                        .filter(Boolean)
                        .join(', ')
                    : [
                        companyProfile.companyDetails.address.state,
                        companyProfile.companyDetails.address.townName,
                        companyProfile.companyDetails.address.street,
                        companyProfile.companyDetails.address.buildingNumber,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                </span>
              </div>
            </>
          )}
        </ListTableCell>
      </ListTableRow>

      <ListTableRow>
        <ListTableHeader>{t('profile.company.tabs.crInformation.listTable.crStatus')}</ListTableHeader>
        <ListTableCell>
          <Badge type={companyProfile.companyDetails.companyStatus === 'ACTIVE' ? 'positive' : 'neutral'} filled>
            {t(
              `profile.company.tabs.crInformation.listTable.crStatuses.${companyProfile.companyDetails.companyStatus}`,
            )}
          </Badge>
        </ListTableCell>
      </ListTableRow>

      <ListTableRow>
        <ListTableHeader>{t('profile.company.tabs.crInformation.listTable.crExpiryDate')}</ListTableHeader>
        <ListTableCell>{formatIsoDate(companyProfile.companyDetails.companyExpiry, i18n.language)}</ListTableCell>
      </ListTableRow>

      <ListTableRow>
        <ListTableHeader>{t('profile.company.tabs.crInformation.listTable.legalType')}</ListTableHeader>
        <ListTableCell>
          {i18n.language === 'ar'
            ? companyProfile.companyDetails.legalTypeAr
            : companyProfile.companyDetails.legalTypeEn}
        </ListTableCell>
      </ListTableRow>

      <ListTableRow>
        <ListTableHeader>{t('profile.company.tabs.crInformation.listTable.signatories')}</ListTableHeader>
        <ListTableCell>
          <div className="flex flex-col">
            {(i18n.language === 'ar'
              ? companyProfile.companyDetails.signatoriesAr
              : companyProfile.companyDetails.signatoriesEn
            ).map((signatory) => (
              <span key={signatory}>{signatory}</span>
            ))}
          </div>
        </ListTableCell>
      </ListTableRow>
    </ListTable>
  );
}
